import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Radio, TriangleAlert, UserCheck } from 'lucide-react';
import MetricCard from '../../components/MetricCard/MetricCard';
import { useI18n } from '../../i18n/I18nContext';
import { formatNumber, formatTime } from '../../utils/datetime';

// The students' days are Asia/Tashkent days: so is the "updated" clock (the server's own stamp).
const STUDENT_TIME_ZONE = 'Asia/Tashkent';

// One tile per KPI of GET /crm/students/summary and the Directory filter it opens (the URL names are
// the API's own). `onlineNow` is absent from the response for a viewer who only gets days.
const TILES = [
  { key: 'onlineNow', filter: 'activity=online', tone: 'success', Icon: Radio },
  { key: 'activeToday', filter: 'activity=today', tone: 'info', Icon: UserCheck },
  { key: 'inactive7d', filter: 'activity=inactive7', tone: 'brand', Icon: Clock },
  { key: 'atRisk', filter: 'atRisk=true', tone: 'coral', Icon: TriangleAlert },
];

/** The tiles a summary can draw; none when the viewer has no students. */
export function pulseTiles(summary) {
  if (!summary || !summary.total) return [];
  return TILES.filter((tile) => Number.isFinite(summary[tile.key]));
}

/**
 * The students' pulse: how many of the students the viewer may open are online, active today, away
 * for a week or at risk, each a link into the Directory with that filter set. The line under the
 * title names the whole group the four numbers are a part of, and when they were counted. It draws
 * the tiles the summary has and nothing when there are none; `loading` holds the row's place
 * meanwhile (the title is known, so it is already there and nothing jumps when the numbers land).
 */
function StudentPulse({ summary, loading }) {
  const { t } = useI18n();
  const tiles = pulseTiles(summary);

  if (loading) {
    return (
      <section className="dash-pulse" aria-busy="true">
        <div className="section-head dash-pulse__head">
          <h2 className="section-head__title">{t('dashboard.pulse.title')}</h2>
          <div className="ui-skel dash-pulse__skel-desc" aria-hidden="true" />
        </div>
        <div className="kpi-row">
          {TILES.map((tile) => <MetricCard key={tile.key} loading className="dash-pulse__skel-tile" />)}
        </div>
      </section>
    );
  }
  if (tiles.length === 0) return null;

  const stamp = summary.meta?.generatedAt;
  const total = t('dashboard.pulse.total', { count: formatNumber(summary.total) });
  return (
    <section className="dash-pulse" aria-label={t('dashboard.pulse.title')}>
      <div className="section-head dash-pulse__head">
        <h2 className="section-head__title">{t('dashboard.pulse.title')}</h2>
        <p className="section-head__desc">
          {total}
          {stamp && (
            <>
              <span className="dash-pulse__dot" aria-hidden="true">·</span>
              <span title={t('dashboard.pulse.tashkentTime')}>
                {t('dashboard.pulse.updated', { time: formatTime(stamp, { timeZone: STUDENT_TIME_ZONE }) })}
              </span>
            </>
          )}
        </p>
        <Link to="/students" className="section-head__action">
          {t('dashboard.pulse.openDirectory')}
          <ArrowRight className="dash-action-arrow" size={14} aria-hidden="true" />
        </Link>
      </div>
      <div className="kpi-row">
        {tiles.map(({ key, filter, tone, Icon }) => (
          <MetricCard
            key={key}
            to={`/students?${filter}`}
            title={t(`dashboard.pulse.${key}`)}
            value={formatNumber(summary[key])}
            icon={<Icon size={20} aria-hidden="true" />}
            tone={tone}
          />
        ))}
      </div>
    </section>
  );
}

export default StudentPulse;
